(() => {
  if (customElements.get('oracle-ticker')) return;
  class OracleTicker extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.source = this.querySelector('.ot-source');
      this.rail = this.querySelector('.ot-rail');
      this.track = this.querySelector('.ot-track');
      this.button = this.querySelector('.ot-toggle');
      this.speed = Math.min(100,Math.max(20,Number(this.dataset.speed)||35));
      this.direction = this.dataset.direction === 'right' ? 'right' : 'left';
      this.paused = this.paused || false;
      this.offset = this.offset || 0;
      this.elapsed = this.elapsed || 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.editorPaused = false;
      this.button.addEventListener('click', () => {
        this.paused = !this.paused;
        this.reconcile();
      }, {signal});
      this.motion.addEventListener('change', () => this.reconcile(), {signal});
      document.addEventListener('visibilitychange', () => this.reconcile(), {signal});
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(this)) this.disconnectedCallback();
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        if (this.contains(event.target)) { this.editorPaused = true; this.reconcile(); }
      }, {signal});
      document.addEventListener('shopify:block:deselect', event => {
        if (this.contains(event.target)) { this.editorPaused = false; this.reconcile(); }
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.reconcile();
      });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this);
      document.fonts?.ready.then(() => { if (this.abort) this.build(); });
      this.setAttribute('data-ready','');
      this.reconcile();
    }
    build() {
      if (!this.abort || this.motion.matches || this.editorPaused) return;
      cancelAnimationFrame(this.frame);
      this.last = null;
      const messages = [...this.source.querySelectorAll('li')].map(item => item.textContent.trim()).filter(Boolean);
      this.track.replaceChildren();
      if (!messages.length) {
        this.removeAttribute('data-moving');
        this.button.disabled = true;
        this.button.textContent = 'Banner empty';
        cancelAnimationFrame(this.frame);
        return;
      }
      this.setAttribute('data-moving','');
      const group = document.createElement('div');
      group.className = 'ot-group';
      const append = () => messages.forEach(text => {
        const span = document.createElement('span');
        span.className = 'ot-message';
        span.textContent = text;
        group.append(span);
      });
      append();
      this.track.append(group);
      const segment = group.getBoundingClientRect().width;
      const viewport = this.rail.getBoundingClientRect().width;
      if (!segment || !viewport) {
        this.removeAttribute('data-moving');
        return;
      }
      const count = Math.max(1,Math.ceil(viewport/segment)+1);
      for (let i=1;i<count;i++) append();
      const oldWidth = this.groupWidth || 1;
      this.groupWidth = group.getBoundingClientRect().width;
      this.offset = ((this.offset/oldWidth)%1)*this.groupWidth;
      this.track.append(group.cloneNode(true));
      this.draw();
      this.start();
    }
    reconcile() {
      cancelAnimationFrame(this.frame);
      this.last = null;
      const staticMode = this.motion.matches || this.editorPaused;
      this.button.disabled = this.motion.matches || this.editorPaused;
      this.button.textContent = this.motion.matches ? 'Motion off' : this.editorPaused ? 'Editing message' : this.paused ? 'Resume banner' : 'Pause banner';
      this.button.setAttribute('aria-pressed', String(this.paused || staticMode));
      if (staticMode) {
        this.style.setProperty('--ot-sheen','-120%');
        this.removeAttribute('data-moving');
        return;
      }
      this.build();
    }
    draw() {
      const x = this.direction === 'right' ? this.offset-this.groupWidth : -this.offset;
      this.track.style.transform = `translate3d(${x}px,0,0)`;
      this.style.setProperty('--ot-sheen', `${((this.elapsed%8)/8)*240-120}%`);
    }
    start() {
      cancelAnimationFrame(this.frame);
      this.last = null;
      if (!this.groupWidth || this.paused || this.motion.matches || this.editorPaused || !this.visible || document.hidden) return;
      this.frame = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      if (!this.abort || this.paused || this.motion.matches || this.editorPaused || !this.visible || document.hidden) return;
      if (this.last !== null) {
        const delta = Math.min(.1,(time-this.last)/1000);
        this.offset = (this.offset+delta*this.speed)%this.groupWidth;
        this.elapsed += delta;
      }
      this.last = time;
      this.draw();
      this.frame = requestAnimationFrame(next => this.tick(next));
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  }
  customElements.define('oracle-ticker', OracleTicker);
})();
